import { useState, useMemo } from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Target,
  CheckCircle2,
  Clock,
  Loader2,
  Layers,
  X,
  Swords,
  AlertCircle,
} from "lucide-react";

/**
 * MicrocycleSelector
 *
 * A modal/overlay that displays all upcoming (and recent past) match-based
 * microcycles from the club calendar.  Each microcycle is anchored to a
 * target match and spans up to 7 days.  The coach picks one to work on.
 */
export default function MicrocycleSelector({
  isDarkMode,
  isOpen,
  onClose,
  microcycles = [],
  loading = false,
  onSelectMicrocycle,
  activeMicrocycleMatchDate = null,
}) {
  const [filter, setFilter] = useState("upcoming"); // "all" | "upcoming" | "past"

  const overlay = "fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm";
  const modal = isDarkMode
    ? "bg-gray-900 border-gray-700 text-gray-100"
    : "bg-white border-gray-200 text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const cardBase = isDarkMode ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200";
  const cardHover = isDarkMode ? "hover:bg-gray-800 hover:border-gray-600" : "hover:bg-white hover:border-gray-300";

  const filtered = useMemo(() => {
    if (!microcycles?.length) return [];
    if (filter === "upcoming") return microcycles.filter((m) => !m.is_past);
    if (filter === "past") return microcycles.filter((m) => m.is_past);
    return microcycles;
  }, [microcycles, filter]);

  // Group by month
  const grouped = useMemo(() => {
    const groups = {};
    for (const mc of filtered) {
      const d = new Date(mc.match_date + "T00:00:00");
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
      if (!groups[key]) groups[key] = { key, label, items: [] };
      groups[key].items.push(mc);
    }
    return Object.values(groups).sort((a, b) => a.key.localeCompare(b.key));
  }, [filtered]);

  if (!isOpen) return null;

  const formatDate = (ds) => {
    const d = new Date(ds + "T00:00:00");
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  };

  const getOpponentName = (mc) => {
    const info = mc.match_info;
    if (!info) return "Match";
    return info.opponent?.name || info.label || "Match";
  };

  const getStatusBadge = (mc) => {
    if (mc.has_active_simulation) {
      const activeScenario = mc.scenarios?.find((s) => s.is_active && s.run_status === "done");
      if (activeScenario) {
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-medium text-emerald-400">
            <CheckCircle2 size={12} /> Simulation active
          </span>
        );
      }
      const runningScenario = mc.scenarios?.find((s) => s.run_status === "queued" || s.run_status === "running");
      if (runningScenario) {
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/20 px-2 py-0.5 text-xs font-medium text-blue-400">
            <Loader2 size={12} className="animate-spin" /> En cours
          </span>
        );
      }
    }
    if (mc.scenario_count > 0) {
      return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
          isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"
        }`}>
          <Layers size={12} /> {mc.scenario_count} scenario{mc.scenario_count > 1 ? "s" : ""}
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
        isDarkMode ? "bg-amber-900/30 text-amber-400" : "bg-amber-100 text-amber-700"
      }`}>
        <AlertCircle size={12} /> No scenario
      </span>
    );
  };

  const isSelected = (mc) => activeMicrocycleMatchDate === mc.match_date;

  return (
    <div className={overlay} onClick={onClose}>
      <div
        className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border shadow-2xl ${modal}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: isDarkMode ? "#374151" : "#e5e7eb" }}
        >
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${
              isDarkMode ? "bg-indigo-500/20" : "bg-indigo-100"
            }`}>
              <Calendar size={20} className={isDarkMode ? "text-indigo-400" : "text-indigo-600"} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Microcycle Calendar</h2>
              <p className={`text-xs ${muted}`}>
                Select a microcycle to plan your scenarios
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`rounded-lg p-2 transition ${
              isDarkMode ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-100 text-gray-500"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 px-6 pt-4 pb-2">
          {[
            { key: "all", label: "All" },
            { key: "upcoming", label: "Upcoming" },
            { key: "past", label: "Past" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                filter === f.key
                  ? isDarkMode
                    ? "bg-indigo-600 text-white"
                    : "bg-indigo-600 text-white"
                  : isDarkMode
                  ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                  : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 size={32} className={`animate-spin ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
              <p className={muted}>Loading microcycles…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Calendar size={40} className={isDarkMode ? "text-gray-600" : "text-gray-300"} />
              <p className={muted}>
                {filter === "upcoming"
                  ? "No upcoming matches in the calendar"
                  : filter === "past"
                  ? "No past matches"
                  : "No matches in the calendar"}
              </p>
              <p className={`text-xs ${muted}`}>
                Add matches to the club calendar to create microcycles
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {grouped.map((group) => (
                <div key={group.key}>
                  <h3 className={`mb-3 text-sm font-semibold uppercase tracking-wider ${muted}`}>
                    {group.label}
                  </h3>
                  <div className="space-y-2">
                    {group.items.map((mc) => {
                      const selected = isSelected(mc);
                      return (
                        <button
                          key={mc.match_date}
                          onClick={() => onSelectMicrocycle(mc)}
                          className={`group w-full rounded-xl border p-4 text-left transition-all ${
                            selected
                              ? isDarkMode
                                ? "border-indigo-500 bg-indigo-950/40 ring-1 ring-indigo-500/50"
                                : "border-indigo-400 bg-indigo-50 ring-1 ring-indigo-400/50"
                              : `${cardBase} ${cardHover}`
                          } ${mc.is_past ? "opacity-70" : ""}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            {/* Left: Match info */}
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              {/* Match day circle */}
                              <div className={`flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl ${
                                mc.is_current
                                  ? "bg-indigo-600 text-white"
                                  : mc.is_past
                                  ? isDarkMode ? "bg-gray-700 text-gray-400" : "bg-gray-200 text-gray-500"
                                  : isDarkMode ? "bg-gray-700 text-gray-200" : "bg-gray-200 text-gray-700"
                              }`}>
                                <span className="text-xs font-medium leading-none">
                                  {new Date(mc.match_date + "T00:00:00").toLocaleDateString("fr-FR", { month: "short" }).toUpperCase()}
                                </span>
                                <span className="text-lg font-bold leading-none">
                                  {new Date(mc.match_date + "T00:00:00").getDate()}
                                </span>
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Swords size={14} className={mc.is_past ? (isDarkMode ? "text-gray-500" : "text-gray-400") : (isDarkMode ? "text-amber-400" : "text-amber-600")} />
                                  <span className="font-semibold truncate">
                                    {getOpponentName(mc)}
                                  </span>
                                  {mc.is_current && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/20 px-2 py-0.5 text-xs font-medium text-indigo-400">
                                      <Target size={10} /> En cours
                                    </span>
                                  )}
                                </div>
                                <p className={`mt-0.5 text-xs ${muted}`}>
                                  <Clock size={11} className="inline mr-1" />
                                  {formatDate(mc.start_date)} → {formatDate(mc.match_date)}
                                  <span className="ml-2">·</span>
                                  <span className="ml-2">{mc.days_count} jour{mc.days_count > 1 ? "s" : ""}</span>
                                </p>

                                {/* Scenario pills */}
                                {mc.scenarios?.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {mc.scenarios.map((s) => (
                                      <span
                                        key={s.id}
                                        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                                          s.is_active
                                            ? "bg-emerald-500/20 text-emerald-400"
                                            : s.run_status === "queued" || s.run_status === "running"
                                            ? "bg-blue-500/20 text-blue-400"
                                            : s.is_past
                                            ? isDarkMode ? "bg-gray-700 text-gray-400" : "bg-gray-200 text-gray-500"
                                            : isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"
                                        }`}
                                      >
                                        {s.is_active ? <CheckCircle2 size={9} /> : s.run_status === "queued" || s.run_status === "running" ? <Loader2 size={9} className="animate-spin" /> : null}
                                        {(s.name || "").replace(/^scenario_/, "")}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Right: Status */}
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              {getStatusBadge(mc)}
                              {selected && (
                                <span className="text-xs font-medium text-indigo-400 mt-1">
                                  Selected
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
